import { useCallback, useRef, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import analyticsClient from 'api/analyticsClient';
import { TRACK_VIEWS } from 'api/analytics/mutations';
import {
  TrackViews,
  TrackViewsVariables,
} from 'api/analytics/types/TrackViews';
import {
  ViewEventCode,
  AnalyticsTargetType,
  ViewEventInput,
} from 'api/graphql-analytics-global-types';

export { ViewEventCode, AnalyticsTargetType };

/**
 * View event codes (opcodes) – mapping for analytics:
 * V_A = Direct view (e.g. opened a single media post / profile page)
 * V_B = Feed view (e.g. post appeared in a feed/list, batched)
 * V_C = Link copy (e.g. user copied link to clipboard)
 * V_D = Watch-time bonus (e.g. watched video 10+ seconds)
 * V_E = Message/share (e.g. user shared the content)
 */

const BATCH_DELAY_MS = 1000;
const WATCH_TIME_THRESHOLD_SECONDS = 10;

interface BatchedView {
  targetId: string;
  viewCode: ViewEventCode;
}

interface UseTrackViewsOptions {
  /** Target type (MEDIA_POST or PROFILE) */
  targetType: AnalyticsTargetType;
  /** Enable batching for feed views (default: true) */
  enableBatching?: boolean;
}

interface UseTrackViewsReturn {
  trackDirectView: (targetId: string) => Promise<void>;
  trackFeedView: (targetId: string) => void;
  /** Send all fetched feed item IDs in one request (e.g. when media feed list loads). */
  trackFeedViewBatch: (targetIds: string[]) => Promise<void>;
  trackLinkCopy: (targetId: string) => Promise<void>;
  trackWatchTimeBonus: (targetId: string) => Promise<void>;
  trackMessageShare: (targetId: string) => Promise<void>;
  flushBatch: () => Promise<void>;
  loading: boolean;
}

/**
 * Hook for tracking views with the Views Algorithm
 * Supports batch operations and different view types with configurable multipliers
 */
export function useTrackViews({
  targetType,
  enableBatching = true,
}: UseTrackViewsOptions): UseTrackViewsReturn {
  const batchQueueRef = useRef<BatchedView[]>([]);
  const batchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [trackViewsMutation, { loading }] = useMutation<
    TrackViews,
    TrackViewsVariables
  >(TRACK_VIEWS, {
    client: analyticsClient,
    // Silently fail - analytics shouldn't break the app
    onError: (error) => {
      console.error('[Analytics] Track views error:', error);
    },
  });

  // Send views to the API
  const sendViews = useCallback(
    async (views: ViewEventInput[]) => {
      if (views.length === 0) return;

      try {
        await trackViewsMutation({
          variables: {
            input: {
              targetType,
              views,
            },
          },
        });
      } catch (error) {
        console.error('[Analytics] Failed to track views:', error);
      }
    },
    [trackViewsMutation, targetType]
  );

  // Flush batched views
  const flushBatch = useCallback(async () => {
    if (batchTimeoutRef.current) {
      clearTimeout(batchTimeoutRef.current);
      batchTimeoutRef.current = null;
    }

    const views = [...batchQueueRef.current];
    batchQueueRef.current = [];

    if (views.length > 0) {
      await sendViews(views);
    }
  }, [sendViews]);

  // Add view to batch queue
  const addToBatch = useCallback(
    (view: BatchedView) => {
      batchQueueRef.current.push(view);

      // Clear existing timeout
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }

      // Set new timeout to flush batch
      batchTimeoutRef.current = setTimeout(() => {
        flushBatch();
      }, BATCH_DELAY_MS);
    },
    [flushBatch]
  );

  const trackDirectView = useCallback(
    async (targetId: string) => {
      await sendViews([{ targetId, viewCode: ViewEventCode.V_A }]);
    },
    [sendViews]
  );

  const trackFeedView = useCallback(
    (targetId: string) => {
      if (enableBatching) {
        addToBatch({ targetId, viewCode: ViewEventCode.V_B });
      } else {
        sendViews([{ targetId, viewCode: ViewEventCode.V_B }]);
      }
    },
    [enableBatching, addToBatch, sendViews]
  );

  const trackFeedViewBatch = useCallback(
    async (targetIds: string[]) => {
      if (targetIds.length === 0) return;
      const views = targetIds.map((targetId) => ({
        targetId,
        viewCode: ViewEventCode.V_B,
      }));
      await sendViews(views);
    },
    [sendViews]
  );

  const trackLinkCopy = useCallback(
    async (targetId: string) => {
      await sendViews([{ targetId, viewCode: ViewEventCode.V_C }]);
    },
    [sendViews]
  );

  const trackWatchTimeBonus = useCallback(
    async (targetId: string) => {
      await sendViews([{ targetId, viewCode: ViewEventCode.V_D }]);
    },
    [sendViews]
  );

  const trackMessageShare = useCallback(
    async (targetId: string) => {
      await sendViews([{ targetId, viewCode: ViewEventCode.V_E }]);
    },
    [sendViews]
  );

  return useMemo(
    () => ({
      trackDirectView,
      trackFeedView,
      trackFeedViewBatch,
      trackLinkCopy,
      trackWatchTimeBonus,
      trackMessageShare,
      flushBatch,
      loading,
    }),
    [
      trackDirectView,
      trackFeedView,
      trackFeedViewBatch,
      trackLinkCopy,
      trackWatchTimeBonus,
      trackMessageShare,
      flushBatch,
      loading,
    ]
  );
}

/**
 * Hook for tracking watch time on video content
 * Automatically tracks the +5 bonus when user watches for 10+ seconds
 */
export function useWatchTimeTracker(targetId: string | null) {
  const watchStartTimeRef = useRef<number | null>(null);
  const hasSentBonusRef = useRef(false);
  const { trackWatchTimeBonus } = useTrackViews({
    targetType: AnalyticsTargetType.MEDIA_POST,
  });

  // Start tracking watch time
  const startWatching = useCallback(() => {
    if (!watchStartTimeRef.current) {
      watchStartTimeRef.current = Date.now();
    }
  }, []);

  // Stop tracking watch time
  const stopWatching = useCallback(() => {
    watchStartTimeRef.current = null;
  }, []);

  // Check if watch time threshold reached and send bonus
  const checkWatchTime = useCallback(async () => {
    if (!targetId || hasSentBonusRef.current || !watchStartTimeRef.current) {
      return;
    }

    const watchedSeconds = (Date.now() - watchStartTimeRef.current) / 1000;

    if (watchedSeconds >= WATCH_TIME_THRESHOLD_SECONDS) {
      hasSentBonusRef.current = true;
      await trackWatchTimeBonus(targetId);
    }
  }, [targetId, trackWatchTimeBonus]);

  // Reset tracker for new content
  const reset = useCallback(() => {
    watchStartTimeRef.current = null;
    hasSentBonusRef.current = false;
  }, []);

  return useMemo(
    () => ({
      startWatching,
      stopWatching,
      checkWatchTime,
      reset,
      WATCH_TIME_THRESHOLD_SECONDS,
    }),
    [startWatching, stopWatching, checkWatchTime, reset]
  );
}

export default useTrackViews;
